import TopNavSimple from '#app/components/top-nav-simple.tsx';

function PrivacyPolicy() {
  return (
    <>
      <TopNavSimple />

      <div className="max-w-4xl mx-auto p-6 text-white/80 mt-20">
        <h1 className="text-3xl font-bold mb-10 text-white">Privacy Policy</h1>
        <p className="text-sm text-white/60 mb-6">
          <strong>Last Updated:</strong> September 10, 2025
        </p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Overview</h2>
          <p className="mb-4">
            CircuitBread Insights is a comprehensive content analytics and
            sponsor relationship management platform designed for the
            electronics education community. This privacy policy explains how we
            collect, use, and protect data when you use our internal analytics
            platform.
          </p>
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 mt-4">
            <p className="font-medium text-blue-200 mb-2">
              📋 Important: Official CircuitBread Accounts Only
            </p>
            <p className="text-sm text-blue-100">
              CircuitBread Insights exclusively accesses and analyzes data from{' '}
              <strong>CircuitBread's own official accounts</strong> across all
              integrated platforms. We do not access, collect, or analyze data
              from any third-party accounts, individual users, or external
              organizations.
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Data Collection</h2>
          <p className="mb-4">
            CircuitBread Insights collects and processes the following types of
            data:
          </p>

          <h3 className="text-lg font-medium mb-3 mt-6">
            Content Analytics Data (CircuitBread Official Accounts Only)
          </h3>
          <ul className="list-disc list-inside space-y-2 pl-4 mb-4">
            <li>
              <strong>CircuitBread YouTube Channel:</strong> Video metrics,
              views, impressions, engagement, thumbnails, titles
            </li>
            <li>
              <strong>CircuitBread Facebook Page:</strong> Post analytics,
              impressions, reach, engagement metrics
            </li>
            <li>
              <strong>CircuitBread Instagram Account:</strong> Content
              performance, views, reach, impressions for posts and reels
            </li>
            <li>
              <strong>CircuitBread TikTok Account:</strong> Video analytics,
              views, engagement metrics, video performance data
            </li>
            <li>
              <strong>CircuitBread LinkedIn Company Page:</strong> Post
              impressions and engagement data
            </li>
            <li>
              <strong>CircuitBread Website:</strong> Article metrics, page
              views, engagement, content performance
            </li>
            <li>
              <strong>CircuitBread Mailchimp Account:</strong> Email campaign
              data, recipient counts, send metrics
            </li>
          </ul>
          <p className="text-sm text-white/70 mt-3 pl-4">
            <strong>Note:</strong> All data collected exclusively from
            CircuitBread's verified, owned accounts. No third-party account data
            is accessed or analyzed.
          </p>

          <h3 className="text-lg font-medium mb-3 mt-6">
            User Authentication Data
          </h3>
          <ul className="list-disc list-inside space-y-2 pl-4 mb-4">
            <li>Email addresses for account authentication</li>
            <li>Encrypted passwords using industry-standard bcrypt hashing</li>
            <li>WebAuthn/Passkey credentials for biometric authentication</li>
            <li>Session tokens and authentication state</li>
            <li>User roles and permissions for access control</li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">
            Business Relationship Data
          </h3>
          <ul className="list-disc list-inside space-y-2 pl-4 mb-4">
            <li>Friend/sponsor contact information and company details</li>
            <li>Sponsored content associations and campaign tracking</li>
            <li>
              Historical performance data across multiple reporting periods
            </li>
            <li>PDF report generation metadata</li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Technical Data</h3>
          <ul className="list-disc list-inside space-y-2 pl-4">
            <li>API access tokens for third-party platform integrations</li>
            <li>Application logs and error tracking data</li>
            <li>File storage metadata for generated reports</li>
            <li>Database performance and caching information</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Data Usage</h2>
          <p className="mb-4">
            We use collected data for the following business purposes:
          </p>

          <h3 className="text-lg font-medium mb-3 mt-6">
            Analytics and Reporting
          </h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>
              Generate comprehensive sponsor performance reports across all
              digital platforms
            </li>
            <li>
              Track content performance metrics for quarterly and all-time
              analytics
            </li>
            <li>
              Create professional PDF reports demonstrating ROI to business
              partners
            </li>
            <li>
              Analyze cross-platform campaign effectiveness and audience
              engagement
            </li>
            <li>
              Monitor badge reach, newsletter performance, and toolbox
              engagement
            </li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Platform Management</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>
              Authenticate and authorize internal users for platform access
            </li>
            <li>Manage friend/sponsor relationships and contact information</li>
            <li>
              Track sponsored content associations across multiple platforms
            </li>
            <li>Maintain historical performance data for trend analysis</li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Data Integration</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>
              Combine metrics from YouTube, Facebook, Instagram, TikTok,
              LinkedIn, and CircuitBread
            </li>
            <li>
              Aggregate email campaign data from Mailchimp for comprehensive
              reach analysis
            </li>
            <li>
              Synchronize data across quarterly and lifetime reporting periods
            </li>
            <li>
              Generate hybrid analytics that combine multiple data sources
            </li>
          </ul>

          <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-4 mt-6">
            <p className="font-medium text-green-200 mb-2">
              🔒 Data Source Clarification
            </p>
            <p className="text-sm text-green-100">
              All data usage is exclusively for internal business analytics and
              sponsor relationship management within the electronics education
              community.{' '}
              <strong>
                We only access CircuitBread's own content and account metrics
              </strong>{' '}
              - we do not collect personal data from end users, followers, or
              the general public.
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">
            Data Storage and Security
          </h2>

          <h3 className="text-lg font-medium mb-3 mt-6">
            Storage Infrastructure
          </h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>
              Primary data stored in MySQL database with encrypted connections
            </li>
            <li>
              File storage using AWS S3-compatible object storage with
              encryption at rest
            </li>
            <li>Session data managed with secure, httpOnly cookies</li>
            <li>Application hosted on secure cloud infrastructure</li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Security Measures</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>
              Multi-factor authentication with support for WebAuthn/Passkeys
            </li>
            <li>Password encryption using industry-standard bcrypt hashing</li>
            <li>
              Role-based access control with permission-level restrictions
            </li>
            <li>
              API tokens stored securely with limited scope and regular rotation
            </li>
            <li>Application monitoring and error tracking with Sentry</li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Data Retention</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>
              Analytics data retained for business reporting and historical
              analysis
            </li>
            <li>
              User authentication data maintained for active account duration
            </li>
            <li>
              Generated reports archived for sponsor relationship management
            </li>
            <li>
              Log data retained according to security monitoring requirements
            </li>
            <li>
              Data purged in accordance with platform-specific retention
              policies
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">
            Data Sharing and Disclosure
          </h2>

          <h3 className="text-lg font-medium mb-3 mt-6">Internal Use Only</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>
              Platform access restricted to authorized CircuitBread personnel
              only
            </li>
            <li>
              Role-based permissions ensure users only access data relevant to
              their responsibilities
            </li>
            <li>
              All internal access logged and monitored for security purposes
            </li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Sponsor Reporting</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>
              Aggregated performance reports shared with legitimate business
              sponsors
            </li>
            <li>
              Reports contain only content performance metrics, not individual
              user data
            </li>
            <li>
              Sponsor reports demonstrate campaign ROI across multiple platforms
            </li>
            <li>
              No granular analytics or personal information shared with external
              parties
            </li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">
            Third-Party Services
          </h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>
              Error tracking data shared with Sentry for application monitoring
            </li>
            <li>
              File storage handled by AWS S3-compatible services with encryption
            </li>
            <li>
              No analytics data shared with third-party services beyond
              operational requirements
            </li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Legal Requirements</h3>
          <p className="pl-4 mb-4">
            We may disclose data if required by law, legal process, or
            government request, or to protect the rights, property, or safety of
            CircuitBread, our users, or others.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">
            Third-Party Integrations
          </h2>
          <p className="mb-4">
            CircuitBread Insights integrates with the following third-party
            services to provide comprehensive analytics:
          </p>

          <h3 className="text-lg font-medium mb-3 mt-6">
            Social Media Platforms
          </h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>YouTube Data API v3 for video analytics and channel metrics</li>
            <li>Facebook Graph API for post performance and page insights</li>
            <li>
              Instagram Graph API for content analytics and business account
              data
            </li>
            <li>TikTok Display API for video performance metrics</li>
            <li>
              LinkedIn Marketing API for post impressions and company page
              analytics
            </li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">
            Content and Communication
          </h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>
              CircuitBread API for article performance data and content
              analytics
            </li>
            <li>
              Mailchimp API for email campaign metrics and audience insights
            </li>
            <li>
              Google Analytics for website traffic and user behavior data (if
              configured)
            </li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">
            Infrastructure and Monitoring
          </h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>
              Sentry for application error tracking and performance monitoring
            </li>
            <li>
              AWS S3-compatible storage for secure file and report storage
            </li>
            <li>Resend API for transactional email delivery (if configured)</li>
          </ul>

          <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-4 mt-4">
            <p className="font-medium text-orange-200 mb-2">
              🔑 Account Access Scope
            </p>
            <p className="text-sm text-orange-100">
              All third-party integrations operate under their respective
              privacy policies and terms of service.{' '}
              <strong>
                API access is limited exclusively to CircuitBread's own verified
                accounts
              </strong>{' '}
              - we do not have access to any other organizations' or
              individuals' accounts on these platforms.
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">
            User Rights and Control
          </h2>
          <p className="mb-4">
            As an internal user of CircuitBread Insights, you have the following
            rights:
          </p>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>
              Right to access your account information and authentication data
            </li>
            <li>
              Right to update your profile information and security settings
            </li>
            <li>
              Right to use multi-factor authentication for enhanced security
            </li>
            <li>Right to log out and terminate active sessions</li>
            <li>
              Right to request account deactivation (contact administrator)
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">
            Data Processing Legal Basis
          </h2>
          <p className="mb-4">
            We process data based on legitimate business interests for
            analytics, reporting, and sponsor relationship management within the
            electronics education community. All data processing is necessary
            for:
          </p>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>Providing comprehensive content analytics services</li>
            <li>Managing business relationships and sponsor reporting</li>
            <li>Ensuring platform security and user authentication</li>
            <li>Improving our analytics capabilities and reporting accuracy</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
          <p className="mb-4">
            For privacy-related inquiries, data access requests, or security
            concerns, contact:
          </p>
          <ul className="list-none space-y-2">
            <li>
              <strong>Email:</strong>{' '}
              <a
                href="mailto:info@insights.circuitbread.com"
                className="underline-offset-4 hover:no-underline"
              >
                info@insights.circuitbread.com
              </a>
            </li>
            <li>
              <strong>Platform:</strong> CircuitBread Insights Internal
              Analytics
            </li>
            <li>
              <strong>Organization:</strong> CircuitBread Electronics Education
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Changes to This Policy</h2>
          <p className="mb-4">
            This privacy policy may be updated to reflect changes in our data
            practices, legal requirements, or platform capabilities. We will
            notify users of any material changes through:
          </p>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>Platform notifications to authenticated users</li>
            <li>Updated "Last Updated" date on this policy</li>
            <li>Email notifications for significant changes (if applicable)</li>
          </ul>
          <p className="mt-4">
            Continued use of the platform after policy updates constitutes
            acceptance of the revised terms.
          </p>
        </section>
      </div>
    </>
  );
}

export default PrivacyPolicy;
